'use client'

import { Check, Gift, LoaderCircle, Plus } from 'lucide-react'
import { formatPrice, formatImageURL, getBuyXTriggerCount, getBuyXGetYRewardRule } from '@/utils/util'
import { useCart } from '@/lib/commerce/hooks'

export default function RewardSelector({ compact }: { compact?: boolean }) {
  const {
    buyXGetYInfo,
    availableOrderRewardItems,
    addRewardItem,
    rewardLoading,
    getRewardPrice,
    isRewardOutOfStock,
    getBuyXGetYRewardState,
  } = useCart()

  const buyXGetYBlocks = (buyXGetYInfo || []).filter((info: any) => {
    if (!info.qualifies || !info.rewardProducts?.length) return false
    const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
    return totalRemaining > 0
  })

  const hasOrderRewards = availableOrderRewardItems?.length > 0

  if (buyXGetYBlocks.length === 0 && !hasOrderRewards) return null

  return (
    <div className="space-y-4">
      {buyXGetYBlocks.map((info: any) => {
        const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
        return (
          <RewardCard
            key={info.discount.id}
            title={info.discount?.name || 'Бэлгийн урамшуулал'}
            subtitle={`${getBuyXTriggerCount(info.discount)} авбал ${getBuyXGetYRewardRule(info)}`}
            statusText={`Нөхцөл хангагдлаа (${info.triggerCount}/${getBuyXTriggerCount(info.discount)})${totalRemaining > 0 ? ` — ${totalRemaining} бэлэг сонгох боломжтой` : ''}`}
            compact={compact}
          >
            {info.rewardProducts.map((reward: any) => {
              const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
              if (isLockedByOther) return null
              return (
                <RewardRow
                  key={reward.id}
                  reward={reward}
                  rewardPrice={rewardPrice}
                  loading={rewardLoading}
                  disabled={!canAdd}
                  outOfStock={isOutOfStock}
                  added={!canAdd && !isOutOfStock}
                  onAdd={() => addRewardItem(
                    {
                      ...reward,
                      discountId: info.discount.id,
                      discountName: info.discount.name,
                      discountBase: info.discount.discountBase,
                      variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock },
                    },
                    'buyXgetY',
                  )}
                  compact={compact}
                />
              )
            })}
          </RewardCard>
        )
      })}

      {hasOrderRewards && (
        <RewardCard
          title="Захиалгын бэлэг"
          subtitle="Захиалгын хөнгөлөлтийн бэлэг"
          compact={compact}
        >
          {availableOrderRewardItems.map((reward: any) => {
            const isOut = isRewardOutOfStock(reward)
            const rewardPrice = getRewardPrice(reward)
            return (
              <RewardRow
                key={reward.id}
                reward={reward}
                rewardPrice={rewardPrice}
                loading={rewardLoading}
                disabled={isOut}
                outOfStock={isOut}
                onAdd={() => addRewardItem(reward, 'order')}
                compact={compact}
              />
            )
          })}
        </RewardCard>
      )}
    </div>
  )
}

function RewardCard({ title, subtitle, statusText, compact, children }: { title: string, subtitle: string, statusText?: string, compact?: boolean, children: React.ReactNode }) {
  return (
    <div
      className={`rounded-xl ${compact ? 'p-3' : 'p-4'}`}
      style={{ background: 'rgba(251,146,60,0.06)', border: '1px dashed rgba(251,146,60,0.4)' }}
    >
      <div className="flex items-start gap-3">
        <div
          className="h-9 w-9 rounded-full grid place-items-center shrink-0"
          style={{ background: 'rgba(251,146,60,0.16)', border: '1px solid rgba(251,146,60,0.3)', color: '#fb923c' }}
        >
          <Gift className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-white leading-tight">{title}</div>
          <div className="mt-0.5 text-[11.5px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{subtitle}</div>
        </div>
      </div>
      {statusText && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{ color: '#6ee7b7' }}>
          <Check className="w-3 h-3" />
          <span className="font-medium">{statusText}</span>
        </div>
      )}
      <div className="space-y-2 mt-3">{children}</div>
    </div>
  )
}

function RewardRow({ reward, rewardPrice, loading, disabled, outOfStock, added, onAdd, compact }: { reward: any, rewardPrice: number, loading: boolean, disabled: boolean, outOfStock: boolean, added?: boolean, onAdd: () => void, compact?: boolean }) {
  const badge = reward.valueType === 'free'
    ? 'Үнэгүй'
    : reward.valueType === 'percent' && reward.valuePercent
      ? `-${reward.valuePercent}%`
      : null
  const buttonText = outOfStock ? 'Дууссан' : added ? 'Нэмэгдсэн' : 'Нэмэх'

  return (
    <div
      className={`flex items-center gap-3 ${compact ? 'p-2.5' : 'p-3'} rounded-lg`}
      style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <div className={`${compact ? 'w-11 h-11' : 'w-14 h-14'} shrink-0 rounded-md overflow-hidden`} style={{ background: 'rgba(255,255,255,0.035)' }}>
        <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={`${compact ? 'text-[12px]' : 'text-[13px]'} font-medium text-white truncate`}>{reward.name}</div>
          {badge && (
            <span
              className="text-[9.5px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded shrink-0"
              style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' }}
            >
              {badge}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span
            className={`${compact ? 'text-[11.5px]' : 'text-[12.5px]'} font-medium tabular-nums`}
            style={{ color: rewardPrice === 0 ? '#34d399' : 'white' }}
          >
            {rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}
          </span>
          {reward.price && rewardPrice < reward.price && (
            <span className="text-[10.5px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {formatPrice(reward.price)}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled || loading}
        className={`inline-flex items-center gap-1.5 ${compact ? 'h-8 px-3 text-[11.5px]' : 'h-9 px-3.5 text-[12px]'} rounded-full font-semibold cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shrink-0`}
        style={added || outOfStock
          ? { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }
          : { background: 'white', color: '#0a0a0c' }}
      >
        {loading ? <LoaderCircle className="h-3 w-3 animate-spin" /> : (!added && !outOfStock && <Plus className="h-3 w-3" />)}
        {buttonText}
      </button>
    </div>
  )
}
